import {computed, Injectable, signal} from '@angular/core';
import {
    AccountInfo,
    Configuration,
    createStandardPublicClientApplication,
    IPublicClientApplication,
    LogLevel
} from '@azure/msal-browser';
import {AppEnvironment} from '../app.constants';

export interface AuthServiceUser {
    username: string;
    email: string;
    roles: string[];
}

const oauthClientId = '612df278-c40e-4782-abc0-59ce0fdc5be9';

/**
 * Configuration object to be passed to MSAL instance on creation.
 * For a full list of MSAL.js configuration parameters, visit:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/configuration.md
 */

@Injectable({
    providedIn: 'root'
})
export class AuthService {

    static theInstance: AuthService | undefined;

    private static LoginByPopup = false
    private static ExpiryBuffer = 60_000;

    private appScopes: string[] = [];

    public readonly appUser = signal<AccountInfo | undefined>(undefined);
    public readonly isAuthenticated = computed(() => !!this.appUser());
    public readonly idToken = computed(() => this.appUser()?.idToken);
    public readonly accessToken = signal<string | undefined>(undefined);

    private pca!: IPublicClientApplication;
    private currentAccessTokenExpiresOn: Date | undefined;
    private refreshTimeout: any;

    constructor() {
        AuthService.theInstance = this;
    }

    /**
     * Initialization, to be called during the application init phase,
     * but needs the fetched configuration object for the MSAL instance.
     */
    async initialize(): Promise<boolean> {
        const oauthClientId = AppEnvironment.authClientId;

        this.appScopes = [
            'openid', 'email', 'offline_access', `${oauthClientId}/.default`,
            // 'api://612df278-c40e-4782-abc0-59ce0fdc5be9/portfolio-mgmt-ui'
        ];

        const msalConfig : Configuration = {
            auth: {
                clientId: oauthClientId,
                authority: `https://login.microsoftonline.com/organizations/v2.0`,
                redirectUri: `${document.baseURI}app/home`,
                // navigateToLoginRequestUrl: true
            },
            cache: { cacheLocation: 'sessionStorage' },
            system: {
                loggerOptions: { loggerCallback: AuthService.loggerCallback }
            },
        };

        this.pca = await createStandardPublicClientApplication(msalConfig);

        // Check that the window is an iframe and not popup
        // It's a problem that when there is a silent token acquisition in an iframe,
        // we must not continue with the normal initialization
        // https://learn.microsoft.com/en-us/entra/identity-platform/msal-js-avoid-page-reloads
        const inIframe = window.self !== window.top;
        const msTestInFrame = window !== window.parent && !window.opener;

        console.log('App is running in iframe', inIframe, msTestInFrame);
        if (msTestInFrame || inIframe) {
            console.log('Initialization aborted because we are in an iframe - that is expected during silent token acquisition');
        } else {
            await this.assertUserLogin();
        }
        return true;
    }

    private setExpiresOn(expiresOn: Date | null | undefined) {
        if (expiresOn) {
            this.currentAccessTokenExpiresOn = new Date(expiresOn.getTime() - AuthService.ExpiryBuffer);
            console.info('Access token expires on:', expiresOn);
        } else {
            this.currentAccessTokenExpiresOn = undefined;
            console.info('Access token does not expire');
        }
    }

    private isAccessTokenValid() {
        return this.accessToken()
            && this.currentAccessTokenExpiresOn
            && this.currentAccessTokenExpiresOn > new Date();
    }

    private async assertUserLogin() {
        if (this.isAccessTokenValid()) {
            this.scheduleRefreshToken();
            return;
        }

        const myAccounts: AccountInfo[] = this.pca.getAllAccounts();

        if (myAccounts.length > 0) {
            this.appUser.set(myAccounts[0]);
            await this.refreshTokenSilently();
        }

        if (!this.accessToken()) {
            try {
                await this.login();
            } catch (error) {
                console.error('Error handling redirect:', error);
            }
        }
    }

    private scheduleRefreshToken() {
        if (!this.currentAccessTokenExpiresOn) {
            return;
        }

        if (this.refreshTimeout) {
            clearTimeout(this.refreshTimeout);
        }

        const timeout = this.currentAccessTokenExpiresOn?.getTime() - Date.now();
        console.info('Scheduling token refresh in', timeout, 'ms');

        if (timeout) {
            this.refreshTimeout = setTimeout(async () => {
                console.debug('Refreshing MSAL token automatically');
                await this.refreshTokenSilently();
            }, timeout);
        }
    }

    async refreshTokenSilently(force: boolean = false) {
        if (this.isAccessTokenValid() && !force) {
            return;
        }

        try {
            console.info('Refreshing MSAL token silently');
            const response = await this.pca.acquireTokenSilent({
                account: this.appUser(),
                scopes: this.appScopes,
                forceRefresh: true
            });
            console.info('Token refreshed successfully');
            this.accessToken.set(response.accessToken);
            this.setExpiresOn(response.expiresOn);
            this.scheduleRefreshToken();

        } catch (e) {
            this.accessToken.set(undefined);
            console.error('Silent token acquisition failed:', e);
        }
    }

    get username(): string | undefined {
        return this.appUser()?.username;
    }

    async login(): Promise<void> {
        const response = await this.pca.handleRedirectPromise();
        if (response == null) {
            await this.pca.loginRedirect({// await this.pca.loginPopup({
                scopes: this.appScopes,
                // domainHint: 'common'
                // loginHint: this.getStoredUsername()
            });
        } else if (response.account) {
            console.log('Login successful:', response);
            this.appUser.set(response.account);
            this.accessToken.set(response.accessToken);
            this.setExpiresOn(response.expiresOn);
            this.scheduleRefreshToken();
        } else {
            throw new Error(response.code)
        }
    }

    async logout() {
        if (AuthService.LoginByPopup) {
            await this.pca.logoutPopup({ account: this.appUser()});
        } else {
            await this.pca.logoutRedirect({ account: this.appUser() });
        }
        this.appUser.set( undefined);
        this.accessToken.set(undefined);
        this.setExpiresOn(undefined);
    }

    static accessTokenStatic(): string {
        return AuthService.theInstance!.accessToken() ?? 'no-access-token-available';
    }

    static loggerCallback(level: LogLevel, message: any, containsPii: boolean) {
        if (containsPii) {
            return;
        }
        switch (level) {
            case LogLevel.Error:
                console.error(message);
                return;
            case LogLevel.Info:
                console.info(message);
                return;
            case LogLevel.Verbose:
                console.debug(message);
                return;
            case LogLevel.Warning:
                console.warn(message);
                return;
        }
    }
}
